import React from 'react';
import { Table, Badge, Button, Avatar } from '@/components/ui';
import type { MaintenanceIssue, IssueStatus, IssueSeverity } from '../types/maintenance-issues.types';

export interface IssueTableProps {
  data: MaintenanceIssue[];
  isLoading: boolean;
  onEdit: (item: MaintenanceIssue) => void;
  onRowClick: (item: MaintenanceIssue) => void;
}

const statusColors: Record<IssueStatus, string> = {
  open: 'bg-red-100 text-red-600',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  pending_review: 'bg-indigo-100 text-indigo-600',
  resolved: 'bg-emerald-100 text-emerald-600',
  closed: 'bg-slate-900 text-white',
  reopened: 'bg-red-50 text-red-500',
  escalated: 'bg-red-200 text-red-800',
};

const statusLabels: Record<IssueStatus, string> = {
  open: 'Đang mở',
  assigned: 'Đã giao',
  in_progress: 'Đang xử lý',
  pending_review: 'Chờ duyệt',
  resolved: 'Đã xử lý',
  closed: 'Đã đóng',
  reopened: 'Mở lại',
  escalated: 'Escalated',
};

const severityColors: Record<IssueSeverity, string> = {
  low: 'bg-slate-100 text-slate-500',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-600',
  critical: 'bg-red-100 text-red-600',
};

export function IssueTable({ data, isLoading, onEdit, onRowClick }: IssueTableProps) {
  return (
    <Table
      columns={[
        { key: 'code', title: 'Mã sự cố' },
        { key: 'title', title: 'Tiêu đề & Khu vực' },
        { key: 'severity', title: 'Mức độ', align: 'center' },
        { key: 'assignee', title: 'Người xử lý' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
        { key: 'sla', title: 'SLA', align: 'center' },
        { key: 'actions', title: '', align: 'right' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'code':
            return <span className="font-mono text-[11px] font-bold text-slate-400  tracking-wider">{item.issue_code}</span>;
          case 'title':
            return (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900 line-clamp-1">{item.title}</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-os-primary font-bold ">{item.issue_type}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[10px] text-slate-500 font-medium">{item.zone?.zone_name || 'Toàn dự án'}</span>
                </div>
              </div>
            );
          case 'severity':
            return (
              <Badge className={`${severityColors[item.severity]} border-transparent text-[10px] font-bold  px-2`}>
                {item.severity}
              </Badge>
            );
          case 'assignee':
            return (
              <div className="flex items-center gap-2">
                <Avatar src={item.assignee?.avatar_url} name={item.assignee?.full_name} size="xs" />
                <span className="text-xs font-bold text-slate-700">{item.assignee?.full_name || 'Chưa phân công'}</span>
              </div>
            );
          case 'status':
            return (
              <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                {statusLabels[item.status_code]}
              </Badge>
            );
          case 'sla':
            return item.is_sla_breach ? (
              <i className="ri-alarm-warning-fill text-red-500 text-lg" title="Vi phạm SLA" />
            ) : (
              <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" title="Trong hạn SLA" />
            );
          case 'actions':
            return (
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); onEdit(item); }} />
              </div>
            );
          default:
            return null;
        }
      }}
    />
  );
}
